<template>
  <div class="koenig-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$Ek = ½Mv_cm² + Σ½m_i(v_i')²$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>质心平动能 (Ek,cm)</label>
              <span class="val">{{ ekTrans.toFixed(1) }} <small>J</small></span>
           </div>
           <div class="hud-item orange">
              <label>绕质心转动能 (Ek,rot)</label>
              <span class="val">{{ ekRot.toFixed(1) }} <small>J</small></span>
           </div>
        </div>

        <svg width="100%" height="260" viewBox="0 0 400 260" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-cm"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <marker id="arr-v" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#38bdf8" /></marker>
          </defs>

          <!-- 质心运动轨迹 -->
          <line x1="20" y1="130" x2="380" y2="130" stroke="#1e293b" stroke-width="1" stroke-dasharray="8,4" />

          <!-- 多质点系统 (System of Particles) -->
          <g :transform="`translate(${cmX}, 130)`">
             <!-- 质心指示 (CM) -->
             <circle r="8" fill="#10b981" filter="url(#glow-cm)" />
             <line x1="0" y1="0" :x2="v_cm * 20" y2="0" stroke="#10b981" stroke-width="3" marker-end="url(#arr-v)" />
             <text y="-15" text-anchor="middle" fill="#10b981" font-size="10" font-weight="900">质心 Center of Mass</text>

             <!-- 绕质心旋转的子系统粒子 -->
             <g v-for="(p, i) in particles" :key="i" :transform="`rotate(${rotation + p.angle}) translate(${radius}, 0)`">
                <!-- 连线 (向内拉力) -->
                <line x1="0" y1="0" x2="-80" y2="0" stroke="#334155" stroke-dasharray="2,2" />
                <circle r="6" :fill="p.color" />
                <!-- 相对速度矢量 v_i' -->
                <line x1="0" y1="0" x2="0" :x2="0" :y2="rotV * 15" stroke="#fbbf24" stroke-width="2" marker-end="url(#arr-v)" />
             </g>
          </g>

          <!-- 能量分解对比图 -->
          <g transform="translate(320, 20)">
             <rect width="60" height="80" fill="rgba(15,23,42,0.8)" rx="4" />
             <rect x="10" :y="70 - (ekTrans/200)*60" width="15" :height="(ekTrans/200)*60" fill="#38bdf8" rx="1" />
             <rect x="35" :y="70 - (ekRot/200)*60" width="15" :height="(ekRot/200)*60" fill="#fbbf24" rx="1" />
             <text x="30" y="78" text-anchor="middle" fill="#64748b" font-size="7">E_cm | E_rot</text>
          </g>
        </svg>

        <div class="physics-caption">
           总动能 = 质心整体平动的动能 + 系统相对于质心转动的内动能
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row">
              <div class="control-group">
                 <label class="s-label">质心推进速度 (V_cm)</label>
                 <el-slider v-model="v_cm" :min="0" :max="5" :step="0.1" size="small"></el-slider>
              </div>
              <div class="control-group">
                 <label class="s-label">绕质心自转速度 (ω)</label>
                 <el-slider v-model="rotV" :min="0" :max="5" :step="0.1" size="small"></el-slider>
              </div>
              <div class="btn-group">
                 <el-button type="primary" size="small" @click="isRunning = !isRunning">{{ isRunning ? '暂停' : '启动仿真' }}</el-button>
                 <el-button size="small" @click="reset">归位</el-button>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-collection"></i>
           <p><strong>实战验证：</strong>柯尼希定理揭示了多质点系能量的独立性。调节“自转速度”，你会发现质心速度 V_cm 并不受影响。这解释了为何研究行星轨道时，我们可以把地球简化为质心点，而研究地球昼夜温差时，必须考虑绕质心的转动内能。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'KoenigTheoremViz',
  data() {
    return {
      v_cm: 2,
      rotV: 1,
      cmX: 50,
      rotation: 0,
      radius: 80,
      isRunning: true,
      particles: [
         { angle: 0, color: '#f43f5e' },
         { angle: 120, color: '#0ea5e9' },
         { angle: 240, color: '#fbbf24' }
      ]
    }
  },
  computed: {
    ekTrans() { return 0.5 * 3 * this.v_cm * this.v_cm * 10; },
    ekRot() { return 0.5 * 3 * this.rotV * this.rotV * 20; }
  },
  methods: {
    reset() {
       this.cmX = 50;
       this.rotation = 0;
       this.isRunning = false;
    },
    animate() {
       if (this.isRunning) {
          this.cmX += this.v_cm * 0.5;
          this.rotation += this.rotV * 1.5;
          if (this.cmX > 320) this.cmX = 50;
       }
       this.animFrame = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.animFrame); }
}
</script>

<style scoped lang="scss">
.koenig-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #166534; border: 1px solid #dcfce7; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 260px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #fbbf24; } } }
.physics-caption { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; .flex-row { display: flex; gap: 15px; align-items: flex-end; .control-group { flex: 1; .s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; } } .btn-group { display: flex; gap: 8px; } } }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 11px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
